import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { toast } from "sonner";
import { Trash2, Plus, ArrowLeft, CalendarIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatDateLong, SUPPORTED_CURRENCIES, currencyForCountry } from "@/lib/format";
import ImageUploader from "@/components/ImageUploader";
import ReturnToWorkspace from "@/components/ReturnToWorkspace";

const EVENT_TYPES = ["concert", "festival", "showcase", "listening"];

const ACCESS_OPTIONS = [
    {
        id: "free",
        label: "Free",
        description: "No entry fee. Register-only.",
    },
    {
        id: "pay_at_door",
        label: "Pay at Door",
        description: "Cash, mobile money or bank transfer collected at the venue.",
    },
    {
        id: "external",
        label: "External / Advance Payment",
        description: "Fans pay via an external link before the event.",
    },
];

// Map form state <-> backend flags
function accessFromFlags(is_free, is_pay_at_door) {
    if (is_free) return "free";
    if (is_pay_at_door) return "pay_at_door";
    return "external";
}
function flagsFromAccess(access) {
    return {
        is_free: access === "free",
        is_pay_at_door: access === "pay_at_door",
    };
}

// Parse an ISO 'YYYY-MM-DD' string safely to a local Date (avoiding TZ shift)
function parseISODate(s) {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
function toISODate(date) {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export default function EventEditor() {
    const { id } = useParams();
    const isNew = !id;
    const { activeArtistId } = useRole();
    const navigate = useNavigate();
    const [venues, setVenues] = useState([]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [form, setForm] = useState({
        name: "",
        description: "",
        image_url: "",
        venue_id: "",
        date: "",
        start_time: "20:00",
        end_time: "23:00",
        capacity: 200,
        event_type: "concert",
        ticket_categories: [{ name: "General Admission", price: "" }],
        currency: "",           // ISO code — empty until venue default resolves or user picks
        currencyLocked: false,  // true once user explicitly picks; venue changes then never overwrite
        access: "external", // 'free' | 'pay_at_door' | 'external'
        payment_instructions: "",
        external_payment_link: "",
        published: true,
    });
    // Snapshot of the loaded event for dirty-state comparison on Edit
    // Event flows. Set once by the load effect; never mutated afterwards.
    const [initialForm, setInitialForm] = useState(null);
    const [loadedStatus, setLoadedStatus] = useState(null); // "draft" | "upcoming" | "past" | null (new)

    useEffect(() => {
        endpoints.listVenues().then((d) => setVenues(d.venues));
    }, []);

    useEffect(() => {
        if (!isNew && id) {
            endpoints.getEvent(id).then((d) => {
                const e = d.event;
                const loaded = {
                    name: e.name,
                    description: e.description,
                    image_url: e.image_url,
                    venue_id: e.venue_id,
                    date: e.date,
                    start_time: e.start_time,
                    end_time: e.end_time,
                    capacity: e.capacity,
                    event_type: e.event_type,
                    ticket_categories: e.ticket_categories?.length ? e.ticket_categories : [],
                    currency: e.currency || "",
                    currencyLocked: !!e.currency,
                    access: accessFromFlags(e.is_free, e.is_pay_at_door),
                    payment_instructions: e.payment_instructions || "",
                    external_payment_link: e.external_payment_link || "",
                    published: e.published,
                };
                setForm(loaded);
                setInitialForm(loaded);
                setLoadedStatus(e.status);
            });
        }
    }, [id, isNew]);

    // Dirty = any tracked field differs from the loaded snapshot. New
    // events are always considered "dirty" from the moment the user types.
    const isDirty = (() => {
        if (isNew) {
            // Track whether the user has touched a required field.
            return !!(form.name || form.venue_id || form.date);
        }
        if (!initialForm) return false;
        const keys = [
            "name", "description", "image_url", "venue_id", "date",
            "start_time", "end_time", "capacity", "event_type",
            "currency", "access", "payment_instructions", "external_payment_link", "published",
        ];
        for (const k of keys) {
            if (JSON.stringify(form[k]) !== JSON.stringify(initialForm[k])) return true;
        }
        if (JSON.stringify(form.ticket_categories) !== JSON.stringify(initialForm.ticket_categories)) return true;
        return false;
    })();

    // Browser-level unsaved-changes guard (refresh/close).
    useEffect(() => {
        if (!isDirty) return;
        const h = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", h);
        return () => window.removeEventListener("beforeunload", h);
    }, [isDirty]);

    // Existing-published-event guard on in-app navigation via the Return
    // to Workspace link + Back link + Cancel actions. We attach a
    // capturing click handler that intercepts anchor clicks inside the
    // editor and prompts before allowing navigation to proceed.
    useEffect(() => {
        if (!isDirty) return;
        const onClickCapture = (e) => {
            const a = e.target.closest && e.target.closest("a[href]");
            if (!a) return;
            // Same-page anchors shouldn't trigger.
            if (a.getAttribute("href")?.startsWith("#")) return;
            const ok = window.confirm("You have unsaved changes to this event. Discard changes?");
            if (!ok) { e.preventDefault(); e.stopPropagation(); }
        };
        document.addEventListener("click", onClickCapture, true);
        return () => document.removeEventListener("click", onClickCapture, true);
    }, [isDirty]);

    // Venue-country → currency suggestion. Only fills when the user has
    // NOT explicitly picked/locked a currency (currencyLocked=false) AND
    // no currency is currently set. Never overwrites an existing choice.
    const activeVenue = venues.find((v) => v.id === form.venue_id);
    const suggestedCurrency = activeVenue ? currencyForCountry(activeVenue.country) : null;
    useEffect(() => {
        if (form.currencyLocked) return;
        if (!suggestedCurrency) return;
        if (form.currency === suggestedCurrency) return;
        setForm((f) => ({ ...f, currency: suggestedCurrency }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestedCurrency, form.currencyLocked]);

    const pickCurrency = (code) => {
        setForm((f) => ({ ...f, currency: code, currencyLocked: true }));
        setErrors((prev) => {
            const { currency: _, ...rest } = prev;
            return rest;
        });
    };

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        // Clear that field's error on edit
        setErrors((prev) => {
            if (!prev[k]) return prev;
            const { [k]: _, ...rest } = prev;
            return rest;
        });
    };

    const setCat = (i, k, v) => {
        setForm((f) => {
            const cats = [...f.ticket_categories];
            cats[i] = { ...cats[i], [k]: v };
            return { ...f, ticket_categories: cats };
        });
    };

    const addCat = () =>
        setForm((f) => ({
            ...f,
            ticket_categories: [...f.ticket_categories, { name: "New Tier", price: "" }],
        }));

    const removeCat = (i) =>
        setForm((f) => ({
            ...f,
            ticket_categories: f.ticket_categories.filter((_, j) => j !== i),
        }));

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Event name is required.";
        if (!form.venue_id) errs.venue_id = "Venue is required.";
        if (!form.date) errs.date = "Event date is required.";
        if (!form.start_time) errs.start_time = "Start time is required.";
        if (!form.end_time) errs.end_time = "End time is required.";
        if (!form.capacity || form.capacity <= 0) errs.capacity = "Capacity must be greater than 0.";
        if (form.access !== "free" && !form.currency) {
            errs.currency = "Select a currency for the ticket price.";
        }
        return errs;
    };

    const submit = async (publishFlag) => {
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            toast.error("Please fix the highlighted fields");
            return;
        }
        setSaving(true);
        try {
            const flags = flagsFromAccess(form.access);
            const payload = {
                name: form.name,
                description: form.description,
                image_url: form.image_url,
                venue_id: form.venue_id,
                date: form.date,
                start_time: form.start_time,
                end_time: form.end_time,
                capacity: form.capacity,
                event_type: form.event_type,
                artist_id: activeArtistId,
                published: publishFlag ?? form.published,
                is_free: flags.is_free,
                is_pay_at_door: flags.is_pay_at_door,
                ticket_categories: form.access === "free" ? [] : form.ticket_categories.map((c) => ({
                    ...c,
                    price: c.price === "" || c.price == null ? 0 : Number(c.price),
                    currency: form.currency,
                })),
                currency: form.access === "free" ? "" : form.currency,
                payment_instructions: form.access === "free" ? "" : form.payment_instructions,
                external_payment_link: form.access === "external" ? form.external_payment_link : "",
            };
            if (isNew) {
                await endpoints.createEvent(payload);
                toast.success("Event created");
            } else {
                await endpoints.updateEvent(id, payload);
                toast.success("Event updated");
            }
            // Clear dirty state so the unsaved-changes guard doesn't fire.
            setInitialForm({ ...form, published: payload.published });
            navigate("/workspace/events");
        } catch {
            toast.error("Could not save event");
        } finally {
            setSaving(false);
        }
    };

    const selectedDate = parseISODate(form.date);
    const dateDisplay = selectedDate ? formatDateLong(form.date) : "Select a date";

    return (
        <div className="pm-fade" data-testid="event-editor-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <Link to="/workspace/events" className="text-[10px] font-mono uppercase text-[#666] hover:text-[#ff4400] flex items-center gap-1" data-testid="editor-back">
                            <ArrowLeft className="w-3 h-3" /> Events
                        </Link>
                        <span className="text-[10px] font-mono text-[#333]">·</span>
                        <ReturnToWorkspace testId="editor-return-to-workspace" />
                    </div>
                    <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">
                        {isNew ? "New event" : "Edit event"}
                    </h1>
                </div>
                <div className="flex gap-2 flex-wrap min-w-0" data-testid="editor-actions">
                    {loadedStatus && loadedStatus !== "draft" ? (
                        <button
                            className="pm-btn pm-btn-primary"
                            onClick={() => submit(true)}
                            disabled={saving || !isDirty}
                            data-testid="editor-update-event"
                        >
                            {saving ? "Saving…" : "Update event"}
                        </button>
                    ) : (
                        <>
                            <button className="pm-btn" onClick={() => submit(false)} disabled={saving} data-testid="editor-save-draft">
                                Save draft
                            </button>
                            <button className="pm-btn pm-btn-primary" onClick={() => submit(true)} disabled={saving} data-testid="editor-publish">
                                {saving ? "Saving…" : "Publish"}
                            </button>
                        </>
                    )}
                </div>
            </header>

            <div className="px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Field label="Event name" error={errors.name}>
                        <input
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            className={`w-full bg-transparent border-b py-2 outline-none text-lg ${errors.name ? "border-[#ff3333] focus:border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                            placeholder="e.g. Kokeb Live"
                            data-testid="editor-name-input"
                            aria-invalid={!!errors.name}
                        />
                    </Field>

                    <Field label="Description">
                        <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="editor-desc-input" />
                    </Field>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Venue" error={errors.venue_id}>
                            <select
                                value={form.venue_id}
                                onChange={(e) => set("venue_id", e.target.value)}
                                className={`w-full bg-transparent border px-3 py-2 outline-none ${errors.venue_id ? "border-[#ff3333] focus:border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                                data-testid="editor-venue-select"
                                aria-invalid={!!errors.venue_id}
                            >
                                <option value="" className="bg-[#141414]">Select venue…</option>
                                {venues.map((v) => (
                                    <option key={v.id} value={v.id} className="bg-[#141414]">{v.name} · {v.city}, {v.country} (cap {v.capacity})</option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Type">
                            <select value={form.event_type} onChange={(e) => set("event_type", e.target.value)} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none" data-testid="editor-type-select">
                                {EVENT_TYPES.map((t) => <option key={t} value={t} className="bg-[#141414]">{t}</option>)}
                            </select>
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Date" error={errors.date} className="md:col-span-1">
                            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="editor-date-trigger"
                                        aria-invalid={!!errors.date}
                                        className={`w-full flex items-center justify-between bg-transparent border px-3 py-2 text-left outline-none transition-colors ${errors.date ? "border-[#ff3333]" : "border-[#2a2a2a] hover:border-[#f4f4f0]"}`}
                                    >
                                        <span className={`text-sm ${selectedDate ? "text-[#f4f4f0]" : "text-[#666]"}`}>
                                            {dateDisplay}
                                        </span>
                                        <CalendarIcon className="w-4 h-4 text-[#a3a3a3] shrink-0" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="start"
                                    className="w-auto p-0 bg-[#141414] border border-[#2a2a2a] rounded-none text-[#f4f4f0]"
                                    data-testid="editor-date-popover"
                                >
                                    <Calendar
                                        mode="single"
                                        selected={selectedDate || undefined}
                                        onSelect={(d) => {
                                            if (d) {
                                                set("date", toISODate(d));
                                                setDatePickerOpen(false);
                                            }
                                        }}
                                        initialFocus
                                        className="bg-[#141414]"
                                    />
                                </PopoverContent>
                            </Popover>
                        </Field>
                        <Field label="Start time" error={errors.start_time}>
                            <input
                                type="time"
                                value={form.start_time}
                                onChange={(e) => set("start_time", e.target.value)}
                                className={`w-full bg-transparent border px-3 py-2 outline-none ${errors.start_time ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                                data-testid="editor-start-input"
                                aria-invalid={!!errors.start_time}
                            />
                        </Field>
                        <Field label="End time" error={errors.end_time}>
                            <input
                                type="time"
                                value={form.end_time}
                                onChange={(e) => set("end_time", e.target.value)}
                                className={`w-full bg-transparent border px-3 py-2 outline-none ${errors.end_time ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                                data-testid="editor-end-input"
                                aria-invalid={!!errors.end_time}
                            />
                        </Field>
                    </div>

                    <Field label="Capacity" error={errors.capacity}>
                        <input
                            type="number"
                            value={form.capacity}
                            onChange={(e) => set("capacity", parseInt(e.target.value || 0))}
                            className={`w-full bg-transparent border px-3 py-2 outline-none ${errors.capacity ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                            data-testid="editor-capacity-input"
                            aria-invalid={!!errors.capacity}
                        />
                    </Field>

                    <Field label="Cover image">
                        <ImageUploader
                            value={form.image_url}
                            onChange={(v) => set("image_url", v)}
                            scope="event"
                            artistId={activeArtistId}
                            aspect="video"
                            testId="editor-image"
                        />
                    </Field>
                </div>

                <div className="space-y-6">
                    <div className="border border-[#2a2a2a] p-4" data-testid="editor-access-section">
                        <div className="text-[10px] font-mono uppercase text-[#666]">Access</div>
                        <RadioGroup
                            value={form.access}
                            onValueChange={(v) => set("access", v)}
                            className="mt-3 space-y-2"
                        >
                            {ACCESS_OPTIONS.map((opt) => (
                                <label
                                    key={opt.id}
                                    htmlFor={`access-${opt.id}`}
                                    data-testid={`editor-access-${opt.id}`}
                                    className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${form.access === opt.id ? "border-[#ff4400] bg-[#141414]" : "border-[#2a2a2a] hover:border-[#f4f4f0]"}`}
                                >
                                    <RadioGroupItem
                                        value={opt.id}
                                        id={`access-${opt.id}`}
                                        className="mt-1 border-[#f4f4f0] text-[#ff4400]"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase text-sm">{opt.label}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1 leading-relaxed">
                                            {opt.description}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </RadioGroup>
                    </div>

                    {form.access !== "free" && (
                        <div className="border border-[#2a2a2a] p-4" data-testid="editor-currency-section">
                            <div className="flex flex-wrap items-baseline gap-2">
                                <div className="text-[10px] font-mono uppercase text-[#666]">Currency</div>
                                {suggestedCurrency && !form.currencyLocked && suggestedCurrency === form.currency && (
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]" data-testid="editor-currency-suggestion">
                                        Suggested from {activeVenue?.country}
                                    </div>
                                )}
                                {form.currencyLocked && (
                                    <div className="text-[10px] font-mono uppercase text-[#ff4400]" data-testid="editor-currency-manual">Manually set</div>
                                )}
                            </div>
                            <select
                                value={form.currency}
                                onChange={(e) => pickCurrency(e.target.value)}
                                className={`w-full mt-2 bg-transparent border px-3 py-2 outline-none font-mono uppercase text-sm ${errors.currency ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                                data-testid="editor-currency-select"
                                aria-invalid={!!errors.currency}
                            >
                                <option value="" className="bg-[#141414]">Select currency…</option>
                                {SUPPORTED_CURRENCIES.map((c) => (
                                    <option key={c.code} value={c.code} className="bg-[#141414]">{c.code} · {c.label}</option>
                                ))}
                            </select>
                            {errors.currency && (
                                <div className="mt-1 text-[10px] font-mono uppercase text-[#ff3333]" data-testid="editor-currency-error">{errors.currency}</div>
                            )}
                            {!suggestedCurrency && activeVenue && !form.currencyLocked && (
                                <div className="mt-2 text-[10px] font-mono uppercase text-[#ffea00]" data-testid="editor-currency-unknown-country">
                                    Currency for {activeVenue?.country} is not on file — please select one.
                                </div>
                            )}
                        </div>
                    )}

                    {form.access !== "free" && (
                        <div className="border border-[#2a2a2a] p-4">
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] font-mono uppercase text-[#666]">Ticket categories</div>
                                <button onClick={addCat} className="pm-btn text-xs" data-testid="editor-add-cat">
                                    <Plus className="w-3 h-3 mr-1" /> Add
                                </button>
                            </div>
                            <div className="mt-3 space-y-3">
                                {form.ticket_categories.map((c, i) => (
                                    <div key={i} className="border border-[#2a2a2a] p-3 space-y-2" data-testid={`editor-cat-${i}`}>
                                        <div className="flex gap-2">
                                            <input value={c.name} onChange={(e) => setCat(i, "name", e.target.value)} placeholder="Tier name" className="flex-1 bg-transparent border-b border-[#2a2a2a] py-1 focus:border-[#ff4400] outline-none text-sm" />
                                            <button onClick={() => removeCat(i)} className="text-[#666] hover:text-[#ff3333]" data-testid={`editor-cat-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                step="0.01"
                                                min="0"
                                                value={c.price ?? ""}
                                                placeholder="0"
                                                onChange={(e) => setCat(i, "price", e.target.value)}
                                                onBlur={(e) => {
                                                    // Strip any leading zeros ("029000" → "29000") once the
                                                    // user commits, without wiping intentional "0" or "0.50".
                                                    const v = e.target.value;
                                                    if (!v) return;
                                                    const num = Number(v);
                                                    if (Number.isNaN(num)) return;
                                                    // Preserve decimals; write back the canonical number.
                                                    setCat(i, "price", String(num));
                                                }}
                                                className="w-24 bg-transparent border-b border-[#2a2a2a] py-1 focus:border-[#ff4400] outline-none text-sm"
                                                data-testid={`editor-cat-price-${i}`}
                                            />
                                            <span className="text-xs font-mono uppercase text-[#a3a3a3]" data-testid={`editor-cat-currency-${i}`}>{form.currency || "—"}</span>
                                        </div>
                                    </div>
                                ))}
                                {form.ticket_categories.length === 0 && (
                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                                        No categories yet. Add at least one so fans see prices.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {form.access !== "free" && (
                        <div className="border border-[#2a2a2a] p-4">
                            <div className="text-[10px] font-mono uppercase text-[#666]">Payment</div>
                            <textarea
                                rows={3}
                                value={form.payment_instructions}
                                onChange={(e) => set("payment_instructions", e.target.value)}
                                placeholder={
                                    form.access === "pay_at_door"
                                        ? "e.g. Cash, mobile money or bank transfer at the door."
                                        : "e.g. Wire in advance to bank X, then reply with receipt."
                                }
                                className="w-full mt-2 bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none text-sm"
                                data-testid="editor-pay-instructions"
                            />
                            {form.access === "external" && (
                                <input
                                    value={form.external_payment_link}
                                    onChange={(e) => set("external_payment_link", e.target.value)}
                                    placeholder="External payment / ticket link"
                                    className="w-full mt-2 bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none text-sm"
                                    data-testid="editor-pay-link"
                                />
                            )}
                            <div className="mt-2 text-[10px] font-mono uppercase text-[#666]">
                                Recorded for organization only. Project Mint does not process payments.
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, error, className = "", children }) {
    return (
        <div className={className} data-testid={error ? "field-with-error" : undefined}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1">{label}</div>
            {children}
            {error && (
                <div
                    className="mt-1 text-xs font-mono text-[#ff3333]"
                    data-testid={`error-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    {error}
                </div>
            )}
        </div>
    );
}
